<script setup lang="ts">
import { computed, getCurrentInstance, ref, watch } from "vue";
import DialogLayout from "@/components/common/DialogLayout.vue";

import {
  ReservationDetail,
  ReservationIdentifiers,
  StagePaymentMethod,
} from "@/models/dto";
import { getAllowedBalancePaymentMethods } from "@/utils/payment";
import MakeCreditCardPayment from "./MakeCreditCardPayment.vue";
import MakeBankPayment from "./MakeBankPayment.vue";
import MakeCheckPayment from "./MakeCheckPayment.vue";
import { PaymentMethodTypeKey } from "@/utils/enum";
import { useGoogleAnalytics, Event } from "@/composables/useGoogleAnalytics";
const { proxy } = getCurrentInstance()!;
const t = proxy.$t.bind(proxy);


const props = defineProps<{
  value: boolean;
  reservationIdentifiers: ReservationIdentifiers[];
  reservation: ReservationDetail;
  balancePaymentMethods: StagePaymentMethod[];
  isBookingContact: boolean;
}>();

const dialogIsOpen = ref(false);

watch(
  () => props.value,
  (newVal) => {
    dialogIsOpen.value = newVal;
    emit("update:value", newVal);
  },
  { immediate: true }
);

const emit = defineEmits(["update:value", "payment-success"]);

const activePaymentMethods = computed(() => {
  return getAllowedBalancePaymentMethods(props.balancePaymentMethods);
});

const close = () => {
  dialogIsOpen.value = false;
  selectedTab.value = 0;
  emit("update:value", false);
};

const handleDialogInput = (value: boolean) => {
  dialogIsOpen.value = value;
  emit("update:value", value);
  if (!value) {
    close();
  }
};

const handlePaymentSuccess = () => {
  emit("payment-success");

  close();
};

const selectedTab = ref(0);
const PAYMENT_TAB_MAP = {
  credit_card: {
    key: 'credit_card',
    label: t("paymentInformation.CREDIT_CARD"),
    component: MakeCreditCardPayment,
  },
  check: {
    key: 'check',
    label: t("paymentInformation.CHECK"),
    component: MakeCheckPayment,
  },
  ach: {
    key: 'ach',
    label: t("paymentInformation.BANK_TRANSFER"),
    component: MakeBankPayment,
  },
};
const PAYMENT_TAB_ORDER = ["credit_card", "check", "ach"];

const paymentTabs = computed(() =>
  PAYMENT_TAB_ORDER
    .filter((key) => activePaymentMethods.value.includes(key as PaymentMethodTypeKey))
    .map((key) => ({ ...PAYMENT_TAB_MAP[key], key }))
);


const analytics = useGoogleAnalytics()
const trackPaymentTab = (selectedKey: string): void => {
  analytics.trackEvent(Event.PaymentTab, {
    paymentTab: selectedKey,
  })
}

 // Track tab changes, but only for check and ach tabs (not credit card which is default)
watch(selectedTab, (newTabIndex, oldTabIndex) => {
  if (oldTabIndex !== undefined && newTabIndex >= 0) {
    const selectedPaymentMethod = paymentTabs.value[newTabIndex];
    const selectedKey = selectedPaymentMethod?.key;

    if (selectedKey === 'check' || selectedKey === 'ach') {
      trackPaymentTab(selectedKey);
    }
  }
});


</script>
<template>
  <DialogLayout
    v-model="dialogIsOpen"
    :title="t('reservations.PAY_REMAINING_BALANCE').toString()"
    @input="handleDialogInput"

  >
    <div class="h-full d-flex flex-column">
      <template v-if="paymentTabs.length > 1">
        <v-tabs v-model="selectedTab">
          <v-tab v-for="(tab, idx) in paymentTabs" :key="tab.label">
            {{ tab.label }}
          </v-tab>
        </v-tabs>
        <div class="flex-grow-1 overflow-hidden">
          <v-tabs-items v-model="selectedTab">
            <v-tab-item v-for="tab in paymentTabs" :key="tab.label">
              <component
                :is="tab.component"
                :reservation="reservation"
                :balance-payment-methods="balancePaymentMethods"
                :is-open="dialogIsOpen"
                @payment-success="handlePaymentSuccess"
                @close="close"
                :is-booking-contact="isBookingContact"
              />
            </v-tab-item>
          </v-tabs-items>
        </div>
      </template>
      <template v-else>
        <component
          :is="paymentTabs[0].component"
          :reservation="reservation"
          :balance-payment-methods="balancePaymentMethods"
          :is-open="dialogIsOpen"
          @payment-success="handlePaymentSuccess"
          @close="close"
          :is-booking-contact="isBookingContact"
        />
      </template>
    </div>
  </DialogLayout>
</template>

<style lang="scss" scoped>
@import "~@/scss/colors";

.v-select {
  ::v-deep .v-input__control {
    .v-input__slot {
      .v-select__slot {
        .v-label {
          line-height: 1 !important;
        }
        .v-input__append-inner {
          align-self: auto;
        }
      }
    }
  }
}

.v-text-field {
  ::v-deep .v-input__control {
    .v-input__slot {
      border: 0px !important;
    }
  }
}

.v-btn.v-btn--disabled.v-btn--has-bg {
  background-color: $gray-lighter !important;
  color: $gray-light !important;
}

.v-tab.v-tab--disabled {
  color: $gray-light !important;
}

.v-tabs {
  ::v-deep .v-tabs-slider-wrapper {
    background-color: $gray !important;
  }
}

.v-list.v-select-list.v-sheet {
  max-width: 437px !important;
}
</style>
